const PROMO_SECOND = 1000;

class PromoCountdown extends HTMLElement {
  constructor() {
    super();
    this.frame = null;
    this.timeout = null;
    this.observer = null;
    this.inViewport = true;
    this.lastState = '';
    this.handleVisibility = this.handleVisibility.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.sectionRoot = this.closest('[data-promo-section]');
    this.activePanel = this.querySelector('[data-countdown-active]');
    this.expiredPanel = this.querySelector('[data-countdown-expired]');
    this.diagnostic = this.querySelector('[data-countdown-diagnostic]');
    this.status = this.querySelector('[data-countdown-status]');
    this.accessibleOutput = this.querySelector('[data-countdown-accessible]');
    this.progress = this.querySelector('[data-countdown-progress]');
    this.showMilliseconds = this.dataset.showMilliseconds === 'true';
    this.millisecondDigits = this.dataset.millisecondDigits === '2' ? 2 : 3;
    this.designMode = this.dataset.designMode === 'true';
    this.previewState = this.dataset.editorPreview || 'auto';
    this.urgentThreshold = Math.max(0, Number(this.dataset.urgentThreshold || 0)) * PROMO_SECOND;

    this.unitNodes = Array.from(this.querySelectorAll('[data-countdown-unit]')).map((node) => ({
      name: node.dataset.countdownUnit,
      node,
      value: node.querySelector('[data-countdown-value]'),
    }));
    this.millisecondNodes = Array.from(this.querySelectorAll('[data-countdown-milliseconds]'));

    if ('IntersectionObserver' in window) {
      this.inViewport = false;
      this.observer = new IntersectionObserver((entries) => {
        this.inViewport = entries.some((entry) => entry.isIntersecting);
        if (this.inViewport) this.restartLoop();
      }, { rootMargin: '150px' });
      this.observer.observe(this);
    }

    document.addEventListener('visibilitychange', this.handleVisibility);
    this.prepareTiming();
    this.prepareEntrance();
    this.update();
  }

  disconnectedCallback() {
    this.initialized = false;
    this.cancelLoop();
    this.observer?.disconnect();
    this.entranceObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.handleVisibility);
  }

  handleVisibility() {
    this.restartLoop();
  }

  prepareTiming() {
    const now = Date.now();
    this.mode = this.dataset.mode === 'session' ? 'session' : 'scheduled';
    this.configurationError = '';

    if (this.designMode && this.previewState !== 'auto') {
      this.startTime = now - 60 * 60 * 1000;
      this.endTime = this.previewState === 'expired' ? now - 1000 : now + 25 * 60 * 60 * 1000;
      return;
    }

    if (this.mode === 'session') {
      this.prepareSessionTiming(now);
      return;
    }

    const start = this.parseScheduledDate(this.dataset.startDate, this.dataset.startTime);
    const end = this.parseScheduledDate(this.dataset.endDate, this.dataset.endTime);

    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      this.configurationError = 'Use valid YYYY-MM-DD dates, HH:MM times, and a UTC offset such as +03:00.';
      return;
    }
    if (end <= start) {
      this.configurationError = 'The countdown end must be later than its start.';
      return;
    }

    this.startTime = start;
    this.endTime = end;
  }

  parseScheduledDate(date, time) {
    const offset = this.dataset.utcOffset || '+03:00';
    const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || '');
    const timeMatch = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(time || '');
    const offsetMatch = /^([+-])(0\d|1[0-4]):([0-5]\d)$/.exec(offset);
    if (!dateMatch || !timeMatch || !offsetMatch) return NaN;

    const [, yearText, monthText, dayText] = dateMatch;
    const [, hourText, minuteText, secondText = '00'] = timeMatch;
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);
    const hour = Number(hourText);
    const minute = Number(minuteText);
    const second = Number(secondText);
    const offsetHour = Number(offsetMatch[2]);
    const offsetMinute = Number(offsetMatch[3]);
    if (month < 1 || month > 12 || hour > 23 || minute > 59 || second > 59) return NaN;
    if (day < 1 || day > new Date(Date.UTC(year, month, 0)).getUTCDate()) return NaN;
    if (offsetHour === 14 && offsetMinute !== 0) return NaN;

    const offsetDirection = offsetMatch[1] === '+' ? 1 : -1;
    const offsetMilliseconds = offsetDirection * ((offsetHour * 60) + offsetMinute) * 60000;
    return Date.UTC(year, month - 1, day, hour, minute, second) - offsetMilliseconds;
  }

  prepareSessionTiming(now) {
    const duration = Math.max(0, Number(this.dataset.sessionDuration || 0));
    if (!duration) {
      this.configurationError = 'The session duration must be greater than zero.';
      return;
    }

    const key = `promo-countdown:${this.dataset.storageKey || 'default'}`;
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(key));
    } catch (_error) {
      saved = null;
    }

    if (!saved || saved.duration !== duration || !Number.isFinite(saved.start) || !Number.isFinite(saved.end)) {
      saved = { duration, start: now, end: now + duration };
      try {
        sessionStorage.setItem(key, JSON.stringify(saved));
      } catch (_error) {
        // In-memory timing remains functional when storage is unavailable.
      }
    }

    this.startTime = saved.start;
    this.endTime = saved.end;
  }

  prepareEntrance() {
    const animation = this.dataset.entranceAnimation || 'none';
    if (animation === 'none' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.classList.add('is-revealed');
      return;
    }

    if (!('IntersectionObserver' in window)) {
      this.classList.add('is-revealed');
      return;
    }

    this.entranceObserver = new IntersectionObserver((entries, observer) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        this.classList.add('is-revealed');
        observer.disconnect();
      }
    }, { threshold: 0.15 });
    this.entranceObserver.observe(this);
  }

  update() {
    this.cancelLoop();
    const now = Date.now();

    if (this.configurationError) {
      this.showConfigurationError();
      return;
    }

    if (this.designMode && this.previewState === 'expired') {
      this.renderExpired();
      return;
    }

    if (this.mode === 'scheduled' && now < this.startTime && !(this.designMode && this.previewState === 'active')) {
      this.setState('upcoming');
      this.sectionRoot?.setAttribute('hidden', '');
      this.scheduleNext(Math.min(this.startTime - now, 60000));
      return;
    }

    this.sectionRoot?.removeAttribute('hidden');
    this.sectionRoot?.classList.remove('is-timer-pending');
    this.diagnostic?.setAttribute('hidden', '');

    const remaining = Math.max(0, this.endTime - now);
    if (remaining <= 0) {
      this.renderExpired();
      return;
    }

    this.setState('active');
    this.activePanel?.removeAttribute('hidden');
    this.expiredPanel?.setAttribute('hidden', '');
    this.renderUnits(remaining);
    this.renderProgress(now);

    const urgent = this.urgentThreshold > 0 && remaining <= this.urgentThreshold;
    this.classList.toggle('is-urgent', urgent);

    if (this.showMilliseconds && this.inViewport && !document.hidden) {
      this.frame = requestAnimationFrame(() => this.update());
    } else {
      const delay = this.showMilliseconds ? 250 : Math.max(50, PROMO_SECOND - (now % PROMO_SECOND) + 10);
      this.scheduleNext(delay);
    }
  }

  renderUnits(remaining) {
    const divisors = {
      weeks: 7 * 24 * 60 * 60 * 1000,
      days: 24 * 60 * 60 * 1000,
      hours: 60 * 60 * 1000,
      minutes: 60 * 1000,
      seconds: PROMO_SECOND,
    };
    let previousDivisor = null;
    const accessibleValues = [];

    this.unitNodes.forEach((unit) => {
      let nextValue;
      if (unit.name === 'milliseconds') {
        nextValue = this.millisecondDigits === 2
          ? Math.floor((remaining % PROMO_SECOND) / 10).toString().padStart(2, '0')
          : Math.floor(remaining % PROMO_SECOND).toString().padStart(3, '0');
      } else {
        const divisor = divisors[unit.name];
        const raw = Math.floor(remaining / divisor);
        const value = previousDivisor ? raw % Math.floor(previousDivisor / divisor) : raw;
        nextValue = value.toString().padStart(2, '0');
        previousDivisor = divisor;
        accessibleValues.push(`${nextValue} ${unit.node.dataset.unitLabel || unit.name}`);
      }
      this.setUnitValue(unit, nextValue);
    });
    if (this.millisecondNodes?.length) {
      const millisecondValue = this.millisecondDigits === 2
        ? Math.floor((remaining % PROMO_SECOND) / 10).toString().padStart(2, '0')
        : Math.floor(remaining % PROMO_SECOND).toString().padStart(3, '0');
      this.millisecondNodes.forEach((node) => {
        if (node.textContent !== millisecondValue) node.textContent = millisecondValue;
      });
    }
    if (this.accessibleOutput) {
      const accessibleText = accessibleValues.join(', ');
      if (this.accessibleOutput.textContent !== accessibleText) this.accessibleOutput.textContent = accessibleText;
    }
  }

  setUnitValue(unit, nextValue) {
    if (!unit.value || unit.value.textContent === nextValue) return;
    unit.value.textContent = nextValue;
    if (unit.name === 'milliseconds' || this.dataset.tickAnimation === 'none') return;
    unit.node.classList.remove('is-changing');
    void unit.node.offsetWidth;
    unit.node.classList.add('is-changing');
  }

  renderProgress(now) {
    if (!this.progress) return;
    const duration = this.endTime - this.startTime;
    const elapsed = Math.min(duration, Math.max(0, now - this.startTime));
    const percentage = duration > 0 ? (elapsed / duration) * 100 : 100;
    this.progress.style.setProperty('--promo-progress', `${percentage}%`);
    this.progress.setAttribute('aria-valuenow', Math.round(percentage).toString());
  }

  renderExpired() {
    this.sectionRoot?.removeAttribute('hidden');
    this.sectionRoot?.classList.remove('is-timer-pending');
    this.setState('expired');
    this.activePanel?.setAttribute('hidden', '');
    this.expiredPanel?.removeAttribute('hidden');
    this.classList.remove('is-urgent');
    if (this.progress) {
      this.progress.style.setProperty('--promo-progress', '100%');
      this.progress.setAttribute('aria-valuenow', '100');
    }
  }

  showConfigurationError() {
    this.cancelLoop();
    if (!this.designMode) {
      this.sectionRoot?.setAttribute('hidden', '');
      return;
    }
    this.sectionRoot?.removeAttribute('hidden');
    this.sectionRoot?.classList.remove('is-timer-pending');
    if (this.diagnostic) {
      this.diagnostic.hidden = false;
      this.diagnostic.textContent = this.configurationError;
    }
    this.activePanel?.setAttribute('hidden', '');
    this.expiredPanel?.setAttribute('hidden', '');
  }

  setState(state) {
    if (this.lastState === state) return;
    this.dataset.state = state;
    this.lastState = state;
    if (this.status && (state === 'active' || state === 'expired')) {
      this.status.textContent = state === 'expired'
        ? (this.dataset.expiredAnnouncement || 'The countdown has ended.')
        : (this.dataset.activeAnnouncement || 'The countdown is active.');
    }
    this.dispatchEvent(new CustomEvent('promo-countdown:state', {
      bubbles: true,
      detail: { state },
    }));
  }

  restartLoop() {
    this.cancelLoop();
    this.update();
  }

  scheduleNext(delay) {
    this.timeout = window.setTimeout(() => this.update(), Math.max(16, delay));
  }

  cancelLoop() {
    if (this.frame) cancelAnimationFrame(this.frame);
    if (this.timeout) clearTimeout(this.timeout);
    this.frame = null;
    this.timeout = null;
  }
}

class PromoCoupon extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.handleBlockSelect = this.handleBlockSelect.bind(this);
    this.handleTimerState = this.handleTimerState.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.codeOutput = this.querySelector('[data-coupon-code]');
    this.copyButton = this.querySelector('[data-copy-button]');
    this.status = this.querySelector('[data-copy-status]');
    this.emptyState = this.querySelector('[data-coupon-empty]');
    this.designMode = this.dataset.designMode === 'true';
    this.options = Array.from(this.querySelectorAll('[data-coupon-option]'))
      .map((node) => ({ node, id: node.dataset.blockId, code: node.dataset.code?.trim() || '' }))
      .filter((option) => option.code);
    this.selectCoupon();
    if (this.querySelector('promo-countdown')?.dataset.state === 'expired' && this.copyButton) {
      this.copyButton.disabled = true;
    }
    this.addEventListener('click', this.handleClick);
    this.addEventListener('promo-countdown:state', this.handleTimerState);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);
  }

  disconnectedCallback() {
    this.initialized = false;
    clearTimeout(this.statusTimer);
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('promo-countdown:state', this.handleTimerState);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
  }

  selectCoupon(forcedBlockId = '') {
    if (!this.options.length) {
      this.codeOutput.textContent = '';
      this.copyButton.disabled = true;
      this.emptyState?.removeAttribute('hidden');
      if (!this.designMode) this.closest('[data-promo-section]')?.classList.add('promo-section--empty');
      return;
    }

    const fingerprint = this.options.map((option) => `${option.id}:${option.code}`).join('|');
    const storageKey = `promo-coupon:${this.dataset.storageKey || 'default'}`;
    let selected = this.options.find((option) => option.id === forcedBlockId);

    if (!selected) {
      try {
        const saved = JSON.parse(sessionStorage.getItem(storageKey));
        if (saved?.fingerprint === fingerprint) {
          selected = this.options.find((option) => option.id === saved.id && option.code === saved.code);
        }
      } catch (_error) {
        selected = null;
      }
    }

    if (!selected) {
      const index = this.randomIndex(this.options.length);
      selected = this.options[index];
    }

    this.selected = selected;
    this.closest('[data-promo-section]')?.classList.remove('promo-section--empty');
    this.codeOutput.textContent = selected.code;
    this.copyButton.disabled = this.querySelector('promo-countdown')?.dataset.state === 'expired';
    this.emptyState?.setAttribute('hidden', '');
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({
        fingerprint,
        id: selected.id,
        code: selected.code,
      }));
    } catch (_error) {
      // Random selection still works without storage persistence.
    }
  }

  randomIndex(length) {
    if (window.crypto?.getRandomValues) {
      const value = new Uint32Array(1);
      window.crypto.getRandomValues(value);
      return value[0] % length;
    }
    return Math.floor(Math.random() * length);
  }

  async handleClick(event) {
    const button = event.target.closest('[data-copy-button]');
    if (!button || !this.contains(button) || button.disabled || !this.selected) return;
    try {
      await this.copyText(this.selected.code);
      this.showStatus(this.dataset.copiedText || 'Code copied.', true);
    } catch (_error) {
      this.showStatus(this.dataset.copyFailedText || 'Copy failed. Select and copy the code manually.', false);
    }
  }

  async copyText(text) {
    if (navigator.clipboard?.writeText && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    }
    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();
    const copied = document.execCommand('copy');
    field.remove();
    if (!copied) throw new Error('Copy command failed');
  }

  showStatus(message, success) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.dataset.success = success.toString();
    clearTimeout(this.statusTimer);
    this.statusTimer = setTimeout(() => {
      this.status.textContent = '';
      delete this.status.dataset.success;
    }, 3000);
  }

  handleBlockSelect(event) {
    const blockId = event.detail?.blockId;
    if (blockId && this.options.some((option) => option.id === blockId)) {
      this.selectCoupon(blockId);
      this.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  handleTimerState(event) {
    if (event.detail?.state === 'expired' && this.copyButton) {
      this.copyButton.disabled = true;
    }
  }
}

if (!customElements.get('promo-countdown')) {
  customElements.define('promo-countdown', PromoCountdown);
}
if (!customElements.get('promo-coupon')) {
  customElements.define('promo-coupon', PromoCoupon);
}
